import { snakeCase } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { calculatorTableColumns } from '../../../../helpers/constants';
import { updateItemsInArray } from '../../../../helpers/global';
import useLocalization from '../../../../hooks/useLocalization';
import { calculatorService } from '../../../../services/calculatorServices';
import DataTable from '../../../common/data-table/DataTable';
import SlidingSideBar from '../../../common/SlidingSideBar/SlidingSideBar';
import AddEditCalculatorSideBar from './AddEditCalculatorSideBar';
import { X } from 'react-bootstrap-icons';

const defaultSortOption = {
  key: 'createdAt',
  order: 'desc'
};

const Calculators = () => {
  const { translate } = useLocalization();
  const [data, setData] = useState([]);
  const [sortOption, setSortOption] = useState(defaultSortOption);
  const [editCalculatorMeta, setEditCalculatorMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCalculatorEdited, setIsCalculatorEdited] = useState(false);
  const [calculatorCategories, setCalculatorCategories] = useState([]);

  const onCalculatorEditClick = calculator => {
    setEditCalculatorMeta({ calculator });
  };

  const columns = useMemo(
    () =>
      calculatorTableColumns({
        onEditOptionClick: onCalculatorEditClick,
        onDeleteOptionClick: () => {},
        allowEdit: true,
        translate
      }),
    [onCalculatorEditClick, translate]
  );

  const getAllCalculators = async () => {
    setLoading(true);
    try {
      const { response, error } = await calculatorService.getAllCalculators();
      setLoading(false);

      if (error) {
        toast.error(error);
        return;
      }

      const sortedCalcs = ['irrCalculator', 'pinuiBinui', 'paperApartment', 'rsuCalculator', 'optionsCalculator'];

      setData(sortedCalcs.map(calc => response.find(c => c.type === calc)));
    } catch (e) {
      setLoading(false);
      toast.error('Could not load calculators, please try again!');
      console.log(e);
    }
  };

  useEffect(() => {
    getAllCalculators();
  }, []);

  const onCalculatorChange = updatedCalculator => {
    setData(updateItemsInArray(data, updatedCalculator));
  };

  const handleCategorySubmit = async () => {
    try {
      // Call API to update the category
      const { response, error } = await calculatorService.updateMultipleCategories(calculatorCategories);

      if (error) {
        return toast.error(error);
      }

      if (response) {
        setCalculatorCategories(response.updatedCategories);
      }
    } catch (err) {
      console.error('Error updating category:', err);
      toast.error('Failed to update category');
    }
  };

  const handleCalculatorSave = async (closeSideBar = false) => {
    try {
      const { response, error } = await calculatorService.updateCalculator(
        editCalculatorMeta.calculator._id,
        editCalculatorMeta.calculator
      );

      if (error) {
        const originalCalculator = data.find(c => c._id === editCalculatorMeta.calculator._id);
        setEditCalculatorMeta({ calculator: originalCalculator });
        return toast.error(error);
      }
      onCalculatorChange(response);
      if (closeSideBar) {
        setEditCalculatorMeta(null);
      }

      toast.success('Calculator fields updated successfully!');
      setIsCalculatorEdited(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSave = async () => {
    await handleCalculatorSave();
    await handleCategorySubmit();
  };

  const handleSaveAndClose = async () => {
    await handleCalculatorSave(true);
    await handleCategorySubmit();
  };

  const handleClose = () => {
    setIsCalculatorEdited(false);
    setEditCalculatorMeta(null);
  };

  return (
    <>
      <Card>
        <Card.Header>{translate('calculators')}</Card.Header>
        <Card.Body>
          <DataTable
            columns={columns}
            data={data}
            allowFilter={false}
            allowPagination={false}
            allowSort={false}
            onSortChange={setSortOption}
            sortOptions={sortOption}
            loading={loading}
            headerHorizontalAlign="start"
            dataHorizontalAlign="start"
          />
        </Card.Body>
      </Card>
      <SlidingSideBar
        title={
          editCalculatorMeta?.calculator?.type
            ? `${translate('edit_calculator')} (${translate(snakeCase(editCalculatorMeta.calculator.type))})`
            : translate('edit_calculator')
        }
        visible={Boolean(editCalculatorMeta)}
        fullScreen={true}
        showCloseButton={false}
        customHeaderButtons={
          <CustomHeaderButtons
            onClose={handleClose}
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            translate={translate}
            isCalculatorEdited={isCalculatorEdited}
          />
        }
      >
        {editCalculatorMeta && (
          <AddEditCalculatorSideBar
            calculator={editCalculatorMeta?.calculator}
            onChange={onCalculatorChange}
            setEditCalculatorMeta={setEditCalculatorMeta}
            setIsCalculatorEdited={setIsCalculatorEdited}
            calculatorCategories={calculatorCategories}
            setCalculatorCategories={setCalculatorCategories}
          />
        )}
      </SlidingSideBar>
    </>
  );
};

export default Calculators;

const CustomHeaderButtons = ({ onClose, onSave, onSaveAndClose, translate, isCalculatorEdited }) => {
  return (
    <div className="d-flex align-items-center">
      <Button variant="primary" size="sm" className="me-2 text-white" onClick={onSave} disabled={!isCalculatorEdited}>
        {translate('save')}
      </Button>
      <Button
        variant="success"
        size="sm"
        className="me-2 text-white"
        onClick={onSaveAndClose}
        disabled={!isCalculatorEdited}
      >
        {translate('save_and_close')}
      </Button>
      <X className="btn-close cursor-pointer" size={28} onClick={onClose} />
    </div>
  );
};
